// trialOnboarding.ts
// ---------------------------------------------------------------------------
// "Try it before you commit" guest onboarding. The data layer.
//
// The whole trial runs with NO account and ZERO DB writes. Everything the
// guest does is captured into a JSON *draft* held in sessionStorage. The draft
// is not a new data model. It is a recording of the exact request bodies the
// existing authed endpoints already accept (POST /characters, POST /guilds).
//
// Auth in this app is Discord-OAuth-only, so "sign up" is a full-page redirect
// to Discord and back. sessionStorage survives that same-tab round-trip, so on
// return we "replay" the recorded bodies through the real endpoints in order.
// No creation logic is reimplemented here. We POST the captured payloads to
// the same routes a normal user hits.
//
// Raid creation is intentionally NOT replayed: in the trial it is a guided
// emulation (teaches the flow). The guild IS replayed, so a kept guest lands
// with a real character + guild and can build/edit the raid for real after.
// ---------------------------------------------------------------------------

export const TRIAL_DRAFT_KEY = "trial_onboarding_draft";
export const TRIAL_REPLAY_PENDING_KEY = "trial_replay_pending";
export const TRIAL_DRAFT_VERSION = 1;

export type TrialBranch = "createGuild" | "createRaid" | "invite" | "soloChar";

/** Exact body for POST /characters. */
export interface CharacterPayload {
  gameId: string;
  name: string;
  options: Record<string, number | number[]>;
  metadata: { wcl_url: string | null };
}

/** Exact body for POST /guilds. */
export interface GuildPayload {
  gameId: string;
  name: string;
  tag: string;
  region: string | null;
  serverName: string | null;
  faction: string | null;
  metadata: Record<string, unknown>;
}

/** Exact body for PUT /me/availability. */
export interface AvailabilityPayload {
  slots: string;       // 336-char bitstring (7 days × 48 half-hour slots)
  weekStart: string;   // ISO date of the week's Wednesday
}

/** Emulation-only. Never replayed, kept for the post-signup recap/preview. */
export interface RaidPreview {
  raidLabel: string | null;
  day: string | null;
  time: string | null;
}

export interface TrialDraft {
  version: number;
  createdAt: string;
  branch: TrialBranch;
  gameId: string;
  /** Replayed first. */
  character: CharacterPayload | null;
  /** Replayed after the character (always present once the guild branch ran). */
  guild: GuildPayload | null;
  /** Replayed last. The painted weekly availability. */
  availability?: AvailabilityPayload | null;
  /** Emulation only. Not sent anywhere. */
  raidPreview?: RaidPreview | null;
}

// --- Draft persistence ------------------------------------------------------

export function loadDraft(): TrialDraft | null {
  try {
    const raw = sessionStorage.getItem(TRIAL_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrialDraft;
    if (!parsed || parsed.version !== TRIAL_DRAFT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveDraft(draft: TrialDraft) {
  try {
    sessionStorage.setItem(TRIAL_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / quota, the trial just won't survive a redirect */
  }
}

export function clearDraft() {
  try {
    sessionStorage.removeItem(TRIAL_DRAFT_KEY);
    sessionStorage.removeItem(TRIAL_REPLAY_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function newDraft(gameId: string): TrialDraft {
  return {
    version: TRIAL_DRAFT_VERSION,
    createdAt: new Date().toISOString(),
    branch: "soloChar",
    gameId,
    character: null,
    guild: null,
    availability: null,
    raidPreview: null,
  };
}

// --- Replay-pending flag (survives the Discord OAuth redirect) --------------

export function markReplayPending() {
  try {
    sessionStorage.setItem(TRIAL_REPLAY_PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isReplayPending(): boolean {
  try {
    return sessionStorage.getItem(TRIAL_REPLAY_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearReplayPending() {
  try {
    sessionStorage.removeItem(TRIAL_REPLAY_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

// --- Replay -----------------------------------------------------------------

export interface ReplayStep {
  id: string;
  label: string;
  status: "pending" | "running" | "ok" | "failed" | "skipped";
  error?: string;
}

export interface ReplayResult {
  ok: boolean;
  steps: ReplayStep[];
  /** The created guild row (includes invite_code) when the guild step ran. */
  createdGuild: any | null;
  failedStepId: string | null;
}

async function sendJson(method: "POST" | "PUT", path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

const postJson = (path: string, body: unknown) => sendJson("POST", path, body);

async function readError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    return data.message || data.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/**
 * Replay the captured draft against the real endpoints, in dependency order
 * (character → guild). Stops at the first failure and reports which step broke
 * rather than silently leaving half-state. Best-effort flips the onboarding
 * flag once at least the character landed.
 *
 * Caller is responsible for the session (must be authed) and for clearing the
 * draft / replay-pending flag afterwards.
 */
export async function replayDraft(
  onProgress?: (steps: ReplayStep[]) => void
): Promise<ReplayResult> {
  const draft = loadDraft();

  const steps: ReplayStep[] = [];
  if (draft?.character) steps.push({ id: "character", label: "Creating your character", status: "pending" });
  if (draft?.guild) steps.push({ id: "guild", label: "Setting up your guild", status: "pending" });
  if (draft?.availability) steps.push({ id: "availability", label: "Saving your availability", status: "pending" });

  const emit = () => onProgress?.(steps.map((s) => ({ ...s })));
  emit();

  if (!draft) {
    return { ok: false, steps, createdGuild: null, failedStepId: null };
  }

  let createdGuild: any | null = null;
  let failedStepId: string | null = null;
  let characterOk = false;

  for (const step of steps) {
    step.status = "running";
    emit();
    try {
      if (step.id === "character" && draft.character) {
        const res = await postJson("/characters", draft.character);
        if (!res.ok) throw new Error(await readError(res));
        characterOk = true;
        step.status = "ok";
      } else if (step.id === "guild" && draft.guild) {
        const res = await postJson("/guilds", draft.guild);
        if (!res.ok) throw new Error(await readError(res));
        createdGuild = await res.json().catch(() => null);
        step.status = "ok";
      } else if (step.id === "availability" && draft.availability) {
        const res = await sendJson("PUT", "/me/availability", {
          slots: draft.availability.slots,
          weekStart: draft.availability.weekStart,
          confirm: true,
        });
        if (!res.ok) throw new Error(await readError(res));
        step.status = "ok";
      }
    } catch (err: any) {
      step.status = "failed";
      step.error = err?.message || "Something went wrong";
      failedStepId = step.id;
      emit();
      break;
    }
    emit();
  }

  // Mark any not-yet-run steps as skipped (a prior step failed).
  for (const step of steps) {
    if (step.status === "pending") step.status = "skipped";
  }
  emit();

  // Best-effort: flag onboarding complete if the character made it in.
  if (characterOk) {
    try {
      await postJson("/me/onboarding-complete", {});
    } catch {
      /* non-fatal */
    }
  }

  return {
    ok: failedStepId === null && characterOk,
    steps,
    createdGuild,
    failedStepId,
  };
}
